import React from 'react';
import LoadingSkeleton from './LoadingSkeleton';
import './Card.css';

const VARIANT_DEFAULTS = {
  default: { padding: 'md', shadow: 'sm' },
  elevated: { padding: 'md', shadow: 'xl' },
  interactive: { padding: 'md', shadow: 'sm' },
  compact: { padding: 'compact', shadow: 'none' },
  bordered: { padding: 'md', shadow: 'none' },
};

function Card({
  as: Tag = 'div',
  variant = 'default',
  padding,
  shadow,
  hoverable = false,
  title,
  subtitle,
  actions,
  footer,
  loading = false,
  loadingContent,
  empty = false,
  emptyContent,
  className = '',
  children,
  ...rest
}) {
  const variantDefaults = VARIANT_DEFAULTS[variant] || VARIANT_DEFAULTS.default;
  // padding/shadow are explicit overrides; when omitted they fall back to
  // the variant's own default rather than a fixed literal, so a variant
  // like `compact` (no shadow, compact padding) doesn't get silently
  // overridden by an unrelated hardcoded default.
  const resolvedPadding = padding || variantDefaults.padding;
  const resolvedShadow = shadow || variantDefaults.shadow;

  const classes = [
    'ui-card',
    `ui-card--variant-${variant}`,
    `ui-card--padding-${resolvedPadding}`,
    `ui-card--shadow-${resolvedShadow}`,
    hoverable ? 'ui-card--hoverable' : '',
    className,
  ].filter(Boolean).join(' ');

  const hasHeader = Boolean(title || subtitle || actions);
  const hasSections = hasHeader || Boolean(footer) || loading || empty;

  if (!hasSections) {
    return (
      <Tag className={classes} {...rest}>
        {children}
      </Tag>
    );
  }

  const content = loading
    ? (loadingContent || <LoadingSkeleton variant="paragraph" lines={3} />)
    : empty
      ? (emptyContent || null)
      : children;

  return (
    <Tag className={`${classes} ui-card--has-sections`} {...rest}>
      {hasHeader ? (
        <div className="ui-card__header">
          <div className="ui-card__header-text">
            {title ? <h3 className="ui-card__title">{title}</h3> : null}
            {subtitle ? <p className="ui-card__subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="ui-card__actions">{actions}</div> : null}
        </div>
      ) : null}
      <div className="ui-card__content">{content}</div>
      {footer ? <div className="ui-card__footer">{footer}</div> : null}
    </Tag>
  );
}

export default Card;
